// EXPERIMENT: psf_share_asset_ab_experiment — remove with experiment cleanup
import { useCallback, useMemo } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type {
	EXPERIMENT_PSF_SHARE_ASSET_AB_EXPERIMENT,
	ResolvedExperimentData,
} from '@change-corgi/experiments/configs';

import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

type ShareAssetAbExperiment = ResolvedExperimentData<typeof EXPERIMENT_PSF_SHARE_ASSET_AB_EXPERIMENT>;

/** `Petition.shareAssetAb`: null when the petition is not in the psf_share_asset_ab FCM. */
export type ShareAssetAb =
	| Readonly<{ experimentName: string; variation: string; treated: boolean; override: boolean }>
	| null
	| undefined;

// `useTrackExperimentExposure` needs an experiment on every render. `fallback: true` keeps it from firing.
const NOT_ELIGIBLE: ShareAssetAbExperiment = {
	type: 'experiment-service',
	experimentName: '',
	variation: 'control',
	config: { enabled: false },
	treated: false,
	fallback: true,
	fallbackReason: 'not-enrolled',
};

const VARIATIONS: ReadonlyArray<ShareAssetAbExperiment['variation']> = ['control', 'fake_control', 'variant_1'];

function toVariation(variation: string): ShareAssetAbExperiment['variation'] | undefined {
	return VARIATIONS.find((known) => known === variation);
}

// One treat per experiment per page load, shared by every caller (the slide-up can open several times).
const treats = new Map<string, Promise<void>>();

/**
 * Returns `treat()`, which treats psf_share_asset_ab_experiment and then fires `$exposure`, in every arm, for a
 * sharer on a petition listed in the psf_share_asset_ab FCM. Call it when the sharer gets the share links that carry
 * the arm.
 */
export function useShareAssetAbTreat(shareAssetAb: ShareAssetAb): () => Promise<void> {
	const utilityContext = useUtilityContext();

	// An override session is never treated: `treatExperiment` would register the real assignment, and
	// `useTrackExperimentExposure` never exposes an override. An arm added in Experiment Manager but not here is not
	// treated either, so it can never be counted as another arm.
	const variation = shareAssetAb ? toVariation(shareAssetAb.variation) : undefined;
	const eligible = !!shareAssetAb && !shareAssetAb.override && !!variation;

	// The page data stays `treated: false` after `treatExperiment`, and `useTrackExperimentExposure` no-ops while
	// `!treated`. Only call `trackExposure` after a `true` treat.
	const experimentForExposure = useMemo<ShareAssetAbExperiment>(
		() =>
			eligible
				? {
						type: 'experiment-service',
						experimentName: shareAssetAb.experimentName,
						variation,
						config: { enabled: true },
						treated: true,
					}
				: NOT_ELIGIBLE,
		[eligible, variation, shareAssetAb],
	);
	const trackExposure = useTrackExperimentExposure(experimentForExposure);
	const experimentName = shareAssetAb?.experimentName ?? '';
	const alreadyTreated = !!shareAssetAb?.treated;

	return useCallback(async () => {
		if (!eligible) return undefined;

		let treat = treats.get(experimentName);
		if (!treat) {
			treat = (async () => {
				const report = (error: Error) =>
					void utilityContext.errorReporter.report(
						{ error, context: { feature: 'psf_share_asset_ab_experiment' } },
						{ severity: 'warning' },
					);
				try {
					// `treated: true` means an earlier page load treated it. Skip the treat but expose again, in case
					// that page's `$exposure` was lost. A repeat `$exposure` for the same arm is harmless.
					if (!alreadyTreated && !(await treatExperiment(utilityContext, experimentName))) {
						// `false`: a transport failure, a GraphQL error (GqlClient reports it), a bot session, or an
						// assignment that cannot be treated yet. Free the latch so the next call retries.
						treats.delete(experimentName);
						return;
					}
					if (!(await trackExposure())) report(new Error('Share asset A/B $exposure was not sent'));
				} catch (error) {
					// Only `trackExposure` can throw (`treatExperiment` never rejects), so the latch stays set.
					report(new Error('Share asset A/B $exposure failed', { cause: error }));
				}
			})();
			treats.set(experimentName, treat);
		}
		return treat;
	}, [eligible, experimentName, alreadyTreated, utilityContext, trackExposure]);
}
